import { useEffect, useState, type FormEvent } from 'react';
import { formatInTimeZone, fromZonedTime } from 'date-fns-tz';
import { Ban, Clock3, Pencil, Plus, Settings2, Trash2 } from 'lucide-react';
import { toast } from 'react-toastify';
import ConfirmModal from '../ConfirmModal.tsx';
import {
  createAgendaException,
  deleteAgendaException,
  fetchAgendaExceptions,
  updateAgendaConfig,
  updateAgendaException,
} from '../../services/api.ts';
import type { AgendaConfig, AgendaException, AgendaProfessional } from '../../types';

type AgendaSettingsPanelProps = {
  config: AgendaConfig;
  professionals: AgendaProfessional[];
  onConfigChanged: () => Promise<void> | void;
};

const toLocalInput = (value: string | Date, timezone: string) =>
  formatInTimeZone(value, timezone, "yyyy-MM-dd'T'HH:mm");

const SLOT_INTERVAL_OPTIONS = Array.from({ length: 12 }, (_, index) => (index + 1) * 5);
const COMMON_TIMEZONES = [
  'America/Sao_Paulo',
  'America/Manaus',
  'America/Fortaleza',
  'America/Recife',
  'America/Bahia',
  'America/Cuiaba',
  'America/Porto_Velho',
  'America/Rio_Branco',
];

const AgendaSettingsPanel = ({ config, professionals, onConfigChanged }: AgendaSettingsPanelProps) => {
  const [timezone, setTimezone] = useState(config.timezone);
  const [slotIntervalMinutes, setSlotIntervalMinutes] = useState(config.slotIntervalMinutes);
  const [defaultDurationMinutes, setDefaultDurationMinutes] = useState(config.defaultDurationMinutes);
  const [exceptions, setExceptions] = useState<AgendaException[]>([]);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [type, setType] = useState<'blocked' | 'extra'>('blocked');
  const [professionalId, setProfessionalId] = useState('');
  const [startLocal, setStartLocal] = useState(() => toLocalInput(new Date(), config.timezone));
  const [endLocal, setEndLocal] = useState(() => toLocalInput(new Date(Date.now() + 60 * 60 * 1000), config.timezone));
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<AgendaException | null>(null);

  const loadExceptions = async () => {
    try {
      const now = new Date();
      const from = new Date(now.getTime() - 24 * 60 * 60 * 1000).toISOString();
      const to = new Date(now.getTime() + 93 * 24 * 60 * 60 * 1000).toISOString();
      setExceptions(await fetchAgendaExceptions({ from, to }));
    } catch (error: any) {
      toast.error(error?.response?.data?.error || 'Não foi possível carregar bloqueios e exceções.');
    }
  };

  useEffect(() => {
    setTimezone(config.timezone);
    setSlotIntervalMinutes(config.slotIntervalMinutes);
    setDefaultDurationMinutes(config.defaultDurationMinutes);
  }, [config]);

  useEffect(() => {
    void loadExceptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const saveConfig = async (event: FormEvent) => {
    event.preventDefault();
    setSaving(true);
    try {
      await updateAgendaConfig({ timezone, slotIntervalMinutes, defaultDurationMinutes });
      toast.success('Configurações da agenda atualizadas.');
      await onConfigChanged();
    } catch (error: any) {
      toast.error(error?.response?.data?.error || 'Não foi possível atualizar as configurações.');
    } finally {
      setSaving(false);
    }
  };

  const resetException = () => {
    setEditingId(null);
    setType('blocked');
    setProfessionalId('');
    setStartLocal(toLocalInput(new Date(), config.timezone));
    setEndLocal(toLocalInput(new Date(Date.now() + 60 * 60 * 1000), config.timezone));
    setReason('');
  };

  const editException = (exception: AgendaException) => {
    setEditingId(exception.id);
    setType(exception.type);
    setProfessionalId(exception.professionalId || '');
    setStartLocal(toLocalInput(exception.startAt, config.timezone));
    setEndLocal(toLocalInput(exception.endAt, config.timezone));
    setReason(exception.reason || '');
  };

  const saveException = async (event: FormEvent) => {
    event.preventDefault();
    if (type === 'extra' && !professionalId) {
      toast.error('Selecione um profissional para a disponibilidade extra.');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        type,
        professionalId: professionalId || null,
        startAt: fromZonedTime(startLocal, config.timezone).toISOString(),
        endAt: fromZonedTime(endLocal, config.timezone).toISOString(),
        reason: reason || null,
      };
      if (editingId) await updateAgendaException(editingId, payload);
      else await createAgendaException(payload);
      toast.success(editingId ? 'Exceção atualizada.' : 'Exceção criada.');
      resetException();
      await loadExceptions();
    } catch (error: any) {
      toast.error(error?.response?.data?.error || 'Não foi possível salvar a exceção.');
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setSaving(true);
    try {
      await deleteAgendaException(deleteTarget.id);
      toast.success('Exceção removida.');
      setDeleteTarget(null);
      await loadExceptions();
    } catch (error: any) {
      toast.error(error?.response?.data?.error || 'Não foi possível remover a exceção.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="agenda-settings-stack">
      <section className="agenda-panel">
        <div className="agenda-section-heading">
          <div>
            <span className="agenda-eyebrow"><Settings2 size={15} /> Configuração geral</span>
            <h2>Regras da agenda</h2>
            <p>Horários são armazenados em UTC e exibidos conforme o timezone escolhido.</p>
          </div>
        </div>
        <form className="agenda-form" onSubmit={saveConfig}>
          <div className="agenda-form-grid three-columns">
            <label>
              <span>Timezone</span>
              <input
                value={timezone}
                list="agenda-timezones"
                onChange={(event) => setTimezone(event.target.value)}
                placeholder="America/Sao_Paulo"
                required
              />
              <datalist id="agenda-timezones">
                {COMMON_TIMEZONES.map((item) => <option key={item} value={item} />)}
              </datalist>
            </label>
            <label>
              <span>Grade do calendário</span>
              <select value={slotIntervalMinutes} onChange={(event) => setSlotIntervalMinutes(Number(event.target.value))}>
                {SLOT_INTERVAL_OPTIONS.map((minutes) => <option key={minutes} value={minutes}>{minutes} minutos</option>)}
              </select>
            </label>
            <label>
              <span>Duração padrão</span>
              <input type="number" min={5} max={480} step={5} value={defaultDurationMinutes} onChange={(event) => setDefaultDurationMinutes(Number(event.target.value))} />
            </label>
          </div>
          <div className="agenda-form-actions">
            <button type="submit" className="agenda-button primary" disabled={saving}>{saving ? 'Salvando…' : 'Salvar configurações'}</button>
          </div>
        </form>
      </section>

      <div className="agenda-settings-layout">
        <section className="agenda-panel">
          <div className="agenda-section-heading">
            <div>
              <span className="agenda-eyebrow"><Plus size={15} /> Exceção</span>
              <h2>{editingId ? 'Editar exceção' : 'Novo bloqueio ou horário extra'}</h2>
            </div>
          </div>
          <form className="agenda-form" onSubmit={saveException}>
            <div className="agenda-form-grid">
              <label>
                <span>Tipo</span>
                <select value={type} onChange={(event) => setType(event.target.value as 'blocked' | 'extra')}>
                  <option value="blocked">Bloqueio / ausência</option>
                  <option value="extra">Disponibilidade extra</option>
                </select>
              </label>
              <label>
                <span>Profissional</span>
                <select value={professionalId} onChange={(event) => setProfessionalId(event.target.value)} required={type === 'extra'}>
                  {type === 'blocked' && <option value="">Todos (bloqueio global)</option>}
                  {professionals.filter((item) => item.active || item.id === professionalId).map((professional) => (
                    <option key={professional.id} value={professional.id} disabled={!professional.active}>
                      {professional.name}{professional.active ? '' : ' · inativo'}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                <span>Início</span>
                <input type="datetime-local" value={startLocal} onChange={(event) => setStartLocal(event.target.value)} required />
              </label>
              <label>
                <span>Fim</span>
                <input type="datetime-local" value={endLocal} onChange={(event) => setEndLocal(event.target.value)} required />
              </label>
            </div>
            <label>
              <span>Motivo</span>
              <input value={reason} onChange={(event) => setReason(event.target.value)} maxLength={300} placeholder="Ex.: feriado, férias ou plantão" />
            </label>
            <div className="agenda-form-actions">
              {editingId && <button type="button" className="agenda-button ghost" onClick={resetException}>Cancelar edição</button>}
              <button type="submit" className="agenda-button primary" disabled={saving}>{saving ? 'Salvando…' : editingId ? 'Salvar exceção' : 'Adicionar exceção'}</button>
            </div>
          </form>
        </section>

        <section className="agenda-panel">
          <div className="agenda-section-heading">
            <div>
              <span className="agenda-eyebrow"><Ban size={15} /> Próximos 93 dias</span>
              <h2>Bloqueios e horários extras</h2>
            </div>
          </div>
          {exceptions.length === 0 ? (
            <div className="agenda-empty-state"><Clock3 size={28} /><strong>Nenhuma exceção futura</strong><p>A agenda seguirá somente os horários semanais.</p></div>
          ) : (
            <div className="agenda-card-list">
              {exceptions.map((exception) => {
                const professional = professionals.find((item) => item.id === exception.professionalId);
                return (
                  <article key={exception.id} className="agenda-entity-card">
                    <span className={`agenda-exception-icon ${exception.type}`}><Ban size={16} /></span>
                    <div className="agenda-entity-main">
                      <div className="agenda-entity-title">
                        <strong>{exception.type === 'blocked' ? 'Bloqueio' : 'Horário extra'}</strong>
                        <span className="agenda-badge muted">{professional?.name || 'Todos os profissionais'}</span>
                      </div>
                      <p>{exception.reason || 'Sem motivo informado'}</p>
                      <small>
                        {formatInTimeZone(exception.startAt, config.timezone, 'dd/MM/yyyy HH:mm')} até {formatInTimeZone(exception.endAt, config.timezone, 'dd/MM/yyyy HH:mm')}
                      </small>
                    </div>
                    <div className="agenda-entity-actions">
                      <button type="button" className="agenda-icon-button" onClick={() => editException(exception)} aria-label="Editar exceção"><Pencil size={17} /></button>
                      <button type="button" className="agenda-icon-button danger" onClick={() => setDeleteTarget(exception)} aria-label="Remover exceção"><Trash2 size={17} /></button>
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </section>
      </div>

      <ConfirmModal
        open={Boolean(deleteTarget)}
        title="Remover exceção?"
        description="A disponibilidade voltará a ser calculada sem esta regra."
        confirmLabel="Remover"
        saving={saving}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={confirmDelete}
      />
    </div>
  );
};

export default AgendaSettingsPanel;
